import React, { useEffect, useMemo, useState } from 'react';

import { di } from 'react-magnetic-di';

import { doc, p } from '@atlaskit/adf-utils/builders';
import { Box, xcss } from '@atlaskit/primitives';
import { adfToMarkdownPlus, markdownPlusToAdf } from '@atlassian/adf-markdown-transformer';
import { GicAnywhere } from '@atlassian/gic-anywhere';

import {
	type AiIssueCreateState,
	type ConfluenceAiIssueCreateProps,
	type StateSource,
} from '../../common/types';
import { useFetchAiSuggestedFields } from '../../services/fetch-ai-suggested-fields';
import { useGetConfluencePage } from '../../services/get-confluence-page';

const DEFAULT_TIMEOUT = 7 * 1000; // 7s timeout, typical GIC load is ~3s

const aiIssueCreateWrapperStyle = xcss({
	flexGrow: '1',
});

type ConfluenceAiIssueCreateState = {
	state: AiIssueCreateState;
	error?: Error | unknown;
	[key: string]: any;
};

export const ConfluenceAiIssueCreate = ({
	cloudId,
	pageId,
	highlightedTextAdf = doc(p('')),
	timeoutMs = DEFAULT_TIMEOUT,
	onSuccess,
	onFailure,
	onClose,
	onTimeout,
	testId,
	onStateChange,
}: ConfluenceAiIssueCreateProps) => {
	di(useFetchAiSuggestedFields, GicAnywhere);

	const [confluencePage, setConfluencePage] = useState('');
	const [idMap, setIdMap] = useState({});

	// Custom handler to wrap multiple onStateChange calls
	const handleStateChange = (source: StateSource, state: ConfluenceAiIssueCreateState) => {
		// trigger error if any state is errored
		if (onFailure && state?.state === 'error') {
			onFailure(state.error);
		}

		if (onStateChange) {
			onStateChange(state.state, source);
		}
	};

	// Fetch confluence page
	const { state: confluencePageState, confluencePage: confluencePageAsAdf } = useGetConfluencePage({
		cloudId,
		pageId,
		onStateChange: (newState) => handleStateChange('confluencePage', newState),
	});

	// Effect to transform confluence page ADF to markdown after fetching
	useEffect(() => {
		if (confluencePageState === 'idle' && confluencePageAsAdf) {
			const { markdown, idMap } = adfToMarkdownPlus(confluencePageAsAdf);
			setConfluencePage(markdown);
			setIdMap(idMap);
		}
	}, [confluencePageState, confluencePageAsAdf]);

	// Convert highlighted text to markdown
	const highlightedText = useMemo(() => {
		return adfToMarkdownPlus(highlightedTextAdf).markdown;
	}, [highlightedTextAdf]);

	// Fetch AI suggested fields
	const { state, fields } = useFetchAiSuggestedFields({
		cloudId,
		confluencePage,
		highlightedText,
		onStateChange: (newState) => handleStateChange('aiSuggestedFields', newState),
	});

	const isLoading = confluencePageState === 'loading' || state === 'loading';
	const isErrored = confluencePageState === 'error' || state === 'error';

	return (
		<Box xcss={aiIssueCreateWrapperStyle} testId={testId ?? 'default-confluence-ai-issue-create'}>
			{isLoading && <div>Loading...</div>}
			{isErrored && <div>An error occurred when loading AI Issue Create.</div>}
			{!isLoading && !isErrored && (
				<GicAnywhere
					summary={fields?.summary || ''}
					descriptionAdf={
						fields?.description
							? markdownPlusToAdf({ markdown: fields.description, idMap })
							: doc(p(''))
					}
					timeoutMs={timeoutMs}
					onSuccess={onSuccess}
					onFailure={onFailure}
					onClose={onClose}
					onTimeout={onTimeout}
					consumer={'confluenceHighlight'}
				/>
			)}
		</Box>
	);
};
